import type { PayBasis } from './payslip';

/** How the user says they're paid: the payslip's bases, or by the day or by shift (compared by the hour). */
export type PayBasisAnswer = PayBasis | 'daily' | 'shifts';

/** Answers the user gives in the app; things a payslip can't tell us. */
export type Sector = 'private' | 'public';
/**
 * Special situations with different rules (phase 2 plan 1.1). `youth`, `apprentice` and
 * `adjustedMinimum` have their own minimum wage; the rest aren't supported yet and every
 * check answers "can't check your situation" instead of applying private-sector rules.
 */
export type SpecialStatus = 'youth' | 'apprentice' | 'adjustedMinimum' | 'rehabilitation' | 'cleaningContractor' | 'contractor' | 'foreignWorker' | 'otherArrangement' | 'oldAgePensioner';
/**
 * Statuses whose rules we don't model yet: every check returns statusNotSupported. `otherArrangement` is set by the
 * "Another work arrangement" card of "Who are you?" (MATT GO 2026-10-23 13:40): no checks run for it, on any goal (a stop
 * on the step: src/model/possibleChecks.ts).
 * `oldAgePensioner` (past retirement age, receiving the old-age pension) is checked as usual, plus `pensionerHealthTax`.
 * `cleaningContractor` (a cleaning worker employed through a contractor) is checked as usual, plus the sector minimum
 * (`cleaningMinimumWage`). `contractor` (guarding or institutional catering through a contractor; MATT GO 2026-10-22) gets
 * the general legal checks as a floor, except those a sector rule may set differently (src/rules/status.ts
 * `CONTRACTOR_EXCLUDED`; docs/review/contractor-floor-2026-10-22.md).
 */
export const UNSUPPORTED_STATUSES: readonly SpecialStatus[] = ['rehabilitation', 'otherArrangement'];
/**
 * A foreign worker's sector (or an asylum seeker), asked under the "foreign worker" status: it selects the
 * foreign-worker rule pack (deposit, deduction caps; docs/roadmap.md "workerType"). Caregivers are
 * supported (their employer type is asked; src/model/possibleChecks.ts caregiverEmployer). Israeli workers have none.
 */
export type WorkerType = 'foreignCaregiver' | 'foreignAgriculture' | 'foreignConstruction' | 'foreignIndustry' | 'foreignCooking' | 'asylumSeeker' | 'foreignOther';
export const WORKER_TYPES: readonly WorkerType[] = ['foreignCaregiver', 'foreignAgriculture', 'foreignConstruction', 'foreignIndustry', 'foreignCooking', 'asylumSeeker', 'foreignOther'];
/**
 * Foreign worker types whose rules we don't model: another sector (dead ends part 8: the options cover everyone; it
 * gets "can't check" with the next step, statusNotSupported).
 */
export const UNSUPPORTED_WORKER_TYPES: readonly WorkerType[] = ['foreignOther'];
/** Who employs a foreign caregiver: the patient or family (an individual) or a nursing company. */
export type CaregiverEmployer = 'individual' | 'company';
export const CAREGIVER_EMPLOYERS: readonly CaregiverEmployer[] = ['individual', 'company'];
/**
 * For the `contractor` status: guarding or institutional catering (MATT GO 2026-10-22). A guard's pension, severance and
 * recuperation are checked against the general law as a floor (the 2014 guarding order gives at least as much; params
 * guardingOrder, unconfirmed); catering, or not given, leaves them out (src/rules/status.ts contractorFloor).
 */
export type ContractorSector = 'guarding' | 'catering';
export const CONTRACTOR_SECTORS: readonly ContractorSector[] = ['guarding', 'catering'];
/**
 * Why the job ended (severance, chunk 9). `resigned`: a plain resignation — no severance by law. The others are
 * dismissal or the Severance Pay Law's grounds for a resignation treated as dismissal (s.6 health, s.7 caring
 * for a new child, s.8 moving home, s.9 a fixed-term contract not renewed, s.11 worse conditions or retirement
 * age) and, per the research (unconfirmed), the employer's death or bankruptcy and enlisting in the police or
 * prison service. The app can't verify a ground's conditions: results for them are at most medium.
 */
export type LeavingReason =
  | 'dismissed' | 'resigned' | 'resignedHealth' | 'resignedChildcare' | 'resignedRelocation' | 'fixedTermNotRenewed'
  | 'resignedWorseConditions' | 'resignedRetirementAge' | 'employerDeathOrBankruptcy' | 'resignedEnlistment';
export const LEAVING_REASONS: readonly LeavingReason[] = [
  'dismissed', 'resigned', 'resignedHealth', 'resignedChildcare', 'resignedRelocation', 'fixedTermNotRenewed',
  'resignedWorseConditions', 'resignedRetirementAge', 'employerDeathOrBankruptcy', 'resignedEnlistment',
];

/** Where a foreign worker lives: the housing deduction cap depends on the region (Foreign Workers regulations). */
export type HousingRegion = 'jerusalem' | 'telAviv' | 'haifa' | 'center' | 'south' | 'north';
export const HOUSING_REGIONS: readonly HousingRegion[] = ['jerusalem', 'telAviv', 'haifa', 'center', 'south', 'north'];
/** Statuses with their own (lower) minimum wage; when several apply, the lowest is used. */
export const MINIMUM_WAGE_STATUSES: readonly SpecialStatus[] = ['youth', 'apprentice', 'adjustedMinimum'];
/** Only for the pension minimum age, which the law sets differently for men and women. */
export type Sex = 'male' | 'female';
/**
 * B2/B3 (behind `__F106_GROUPS__`; docs/specs/b2-b3-form106-foreign-palestinian.md §2 Q1): a foreign worker's work visa
 * in one tax year, which sets the class of credit points (src/form106/groupPoints.ts). `b1Both`: caregiving for part of
 * the year, other work for the rest; `b1Part`: a B/1 visa for only part of the time worked.
 */
export type VisaYear = 'b1Caregiving' | 'b1Other' | 'b1Both' | 'b1Part' | 'none' | 'notSure';
export const VISA_YEARS: readonly VisaYear[] = ['b1Caregiving', 'b1Other', 'b1Both', 'b1Part', 'none', 'notSure'];
/** B2/B3 §2 Q2: a foreign worker's months in Israel in one tax year: all 12, the whole months ticked (1–12), or not sure. */
export type MonthsInIsrael = 'all' | readonly number[] | 'notSure';
/** How the employer pays recuperation. */
export type RecuperationPayment = 'yearly' | 'instalments' | 'monthly';

export interface UserAnswers {
  /** Recuperation day value and rules differ by sector; MVP checks cover private only. */
  sector?: Sector;
  /**
   * Did the employee already have a pension arrangement (an active fund)
   * when they started this job? Decides the pension waiting period:
   * yes → covered from day 1 (deposits due retroactively); no → from month 7.
   */
  hadPensionAtHire?: boolean;
  /**
   * Asked when the payslip doesn't make the pay basis clear; when given,
   * it overrides whatever the parser inferred. Paid by the day or by shift: compared by the hour (`resolvePayBasis`).
   */
  payBasis?: PayBasisAnswer;
  /**
   * Asked when the payslip doesn't print a job percentage and it can't be inferred
   * (0–100). Overrides any inference.
   */
  jobPercent?: number;
  /**
   * How recuperation is paid. Overrides the inference from several loaded payslips.
   * `monthly`: each month's payment is compared with the yearly entitlement ÷ 12.
   */
  recuperationPayment?: RecuperationPayment;
  /** Screening step before results (several may apply); empty or absent = none of these. */
  specialStatuses?: readonly SpecialStatus[];
  /**
   * For `youth`: the youth minimum depends on age, day by day. Also asked (optional) of anyone
   * under 21: mandatory pension starts at a minimum age. Not given → treated as an adult.
   */
  birthDate?: import('./dates').IsoDate;
  /** Asked only when the birth date puts the user under 21: the pension minimum age differs (not given → the higher age). */
  sex?: Sex;
  /** The Form 106 sex question was answered "Prefer not to say" (so it is not shown as unanswered; `sex` stays unset). */
  sexDeclined?: boolean;
  /** For `adjustedMinimum`: the percentage of the minimum wage on the assessment letter (30–100). */
  adjustedMinimumPercent?: number;
  /** For `foreignWorker`: the sector (or asylum seeker). Used only while that status is ticked (`foreignWorkerType`). */
  workerType?: WorkerType;
  /**
   * "Were you on unpaid leave or absent without pay this month?" Asked only when a monthly payslip is below the
   * minimum and doesn't show its paid hours (`asksUnpaidLeave`, src/rules/minimumWage.ts). Yes → compared by the
   * hour; no → the monthly minimum; not given → that finding is low confidence.
   */
  unpaidLeave?: boolean;
  /**
   * With `unpaidLeave` yes and several months loaded: the months with unpaid leave or absence (ticked); the other
   * months count as "no". Absent (nothing ticked yet, or one month loaded) → "yes" applies to every month.
   */
  unpaidLeaveMonths?: readonly import('./dates').YearMonth[];
  /**
   * Form 106: what applied to the user in the tax year that can give income tax credit points (asked, never
   * inferred); empty = none of these.
   */
  taxStatuses?: readonly TaxStatus[];
  /**
   * Form 106 refund estimate (behind its build flag): the birth year of each child, when "children" is ticked. Not
   * given → no children's points (the estimate can only understate).
   */
  childrenBirthYears?: readonly number[];
  /** Form 106 refund estimate: the month of aliyah, when "new immigrant" is ticked. Not given → no immigrant points. */
  aliyahMonth?: import('./dates').YearMonth;
  /**
   * Olim (behind `__OLIM__`; docs/specs/olim.md open question 5): asked only while the 2026-2030 exemption pointer shows:
   * whether the person works for a relative or a company they or their family own 25% or more of (Ordinance s.88).
   * It only changes the pointer's notes (that income: a lower cap, and only through the annual return); nothing is computed.
   */
  olimFromRelative?: OlimRelative;
  /**
   * Credit points for a degree (docs/specs/credit-points.md; src/rules/creditDegree.ts), asked under "degree": its kind,
   * the year the studies ended and how many academic years they took. Not given → the Form 106 card asks for them.
   */
  degreeKind?: DegreeKind;
  degreeYear?: number;
  degreeStudyYears?: DegreeStudyYears;
  /**
   * Credit points for a discharged soldier (src/rules/creditSoldier.ts), asked under "finished army or national service":
   * the month the regular service ended, and whether it lasted the full-rate months (23 for a man, 22 for a woman).
   * Month not given → the Form 106 card asks; length not given → the lower rate is counted.
   */
  dischargeMonth?: import('./dates').YearMonth;
  serviceLong?: boolean;
  /** Single parent (src/form106/estimate.ts): the parent the children live with, or the parent living apart who pays support. Not given → not counted. */
  singleParentKind?: SingleParentKind;
  /** Children with a disability (s.45; src/form106/estimate.ts, high estimate only); 3 = three or more. Not given → not counted. */
  disabledChildren?: DisabledChildren;
  /**
   * Form 106: income in the tax year besides the loaded forms' salaries, by tax year (`String(taxYear)`, or
   * `unknown` for a form whose year wasn't read); empty = none of these. Required before any refund signal for that
   * year (docs/form106.md "Income sources").
   */
  incomeSources?: Readonly<Record<string, readonly IncomeSource[]>>;
  /** National Insurance's annual certificate per tax year (same keys as `incomeSources`), when National Insurance pay was ticked. */
  niCertificates?: Readonly<Record<string, import('./form106').NiCertificate>>;
  /**
   * B2/B3 (behind `__F106_GROUPS__`): a foreign worker's work visa per tax year (same keys as `incomeSources`); sets the
   * class of credit points (src/form106/groupPoints.ts). Not asked of an asylum seeker (no B/1 visa).
   */
  visaYears?: Readonly<Record<string, VisaYear>>;
  /** B2/B3: a foreign worker's months in Israel per tax year (same keys); not given → the Form 106 months, with partial ones left out of the low end. */
  monthsInIsrael?: Readonly<Record<string, MonthsInIsrael>>;
  /**
   * Days in the work week (annual leave, sick days): 5 or 6, or fewer than 5 or varying (shifts). Not given, varying,
   * or not read from the payslip (`resolveWorkWeek`) → each check's own safer default (it never over-flags).
   */
  workWeekDays?: 5 | 6 | 'varies';
  /** "This is my last payslip / I left this job": the last day of work (severance). */
  employmentEndDate?: import('./dates').IsoDate;
  /** Why the job ended; asked with the end date. */
  leavingReason?: LeavingReason;
  /** On dismissal: the day the written notice of dismissal was given (the notice period runs from it to the last day). */
  noticeDate?: import('./dates').IsoDate;
  /** For a foreign caregiver: who employs them (decides the deposit and pension arrangement). */
  caregiverEmployer?: CaregiverEmployer;
  /** For a worker employed through a contractor in guarding or institutional catering: which of the two. */
  contractorSector?: ContractorSector;
  /** For a foreign worker with a housing deduction: the region, when the cap there decides the result. */
  housingRegion?: HousingRegion;
}

/** The foreign-worker type that applies: only while the "foreign worker" status is ticked. */
export const foreignWorkerType = (answers: UserAnswers): WorkerType | undefined =>
  (answers.specialStatuses ?? []).includes('foreignWorker') ? answers.workerType : undefined;

/** Olim: the answers to "Do you work for a family member or a family company?" (`olimFromRelative`). */
export const OLIM_RELATIVE = ['yes', 'no', 'notSure'] as const;
export type OlimRelative = (typeof OLIM_RELATIVE)[number];

/** Credit points for a degree: bachelor, master, or anything else (medicine, a certificate, more than one degree, not sure: not counted). */
export const DEGREE_KINDS = ['bachelor', 'master', 'other'] as const;
export type DegreeKind = (typeof DEGREE_KINDS)[number];
/** Single parent: the children live with me, or I live apart from them and pay support. */
export const SINGLE_PARENT_KINDS = ['custody', 'paysSupport'] as const;
export type SingleParentKind = (typeof SINGLE_PARENT_KINDS)[number];
/** How many children with a disability (s.45); 3 = three or more. Not sure = not answered. */
export const DISABLED_CHILDREN = [1, 2, 3] as const;
export type DisabledChildren = (typeof DISABLED_CHILDREN)[number];
/** Academic years of the degree's studies; 3 = three or more. Not sure = not answered. */
export const DEGREE_STUDY_YEARS = [1, 2, 3] as const;
export type DegreeStudyYears = (typeof DEGREE_STUDY_YEARS)[number];

/** Situations that can give income tax credit points beyond the basic ones (Form 106 questions; docs/form106.md). */
export const TAX_STATUSES = ['children', 'singleParent', 'disabledChild', 'degree', 'dischargedSoldier', 'newImmigrant'] as const;
export type TaxStatus = (typeof TAX_STATUSES)[number];

/**
 * Form 106 screening: other income in a tax year (another employer; National Insurance pay for unemployment,
 * maternity, reserve duty or a work injury; a pension; interest, dividends or securities gains taxed at source; home
 * rent on the 10% track or tax-free; other rent or not sure; self-employment; foreign income; anything else). How each
 * affects the refund signals: params form106IncomeSources (docs/research/other-income-filing-exemption.md).
 */
export const INCOME_SOURCES = ['otherEmployer', 'niBenefits', 'pension', 'capitalAtSource', 'rentSeparate', 'rentOther', 'selfEmployment', 'foreignIncome', 'otherIncome'] as const;
export type IncomeSource = (typeof INCOME_SOURCES)[number];
/** The `incomeSources` key of a tax year. */
export const incomeSourcesKey = (taxYear: number | undefined): string => (taxYear === undefined ? 'unknown' : String(taxYear));
